/**
 * postMessage bridge between the TB host widget (`tb-host-widget`) and this
 * iframed SPA.
 *
 * Wire format is **identical** to the TB-EMS `@ems/tb-sdk` bridge so a
 * single host widget can iframe either dashboard. v:1 envelope, both ends
 * tolerate unknown `type` values (forward-compat).
 *
 * Source for the protocol: [TB-EMS/ems-dashboard/packages/tb-sdk/src/bridge.ts](../../../../../Thingsboard/TB-EMS/ems-dashboard/packages/tb-sdk/src/bridge.ts).
 */

export type TbTimeWindow = {
  realtime?: { timewindowMs: number; interval?: number };
  history?:
    | { fixedTimewindow: { startTimeMs: number; endTimeMs: number } }
    | { timewindowMs: number };
};

export type ThemeTokens = {
  primary?: string;
  accent?: string;
  logoUrl?: string;
  fontFamily?: string;
  dark?: boolean;
};

export type CtxUserInfo = {
  tenantId: string;
  customerId?: string;
  authority: string;
};

export type CtxSnapshot = {
  entity: { type: string; id: string } | null;
  aliasId: string | null;
  stateParams: Record<string, string>;
  timeWindow: TbTimeWindow | null;
  settings: Record<string, unknown>;
  userInfo: CtxUserInfo | null;
  locale?: string | null;
};

// host → app -----------------------------------------------------------------
export type HostToApp =
  | { v: 1; type: "ctx.init"; payload: CtxSnapshot }
  | { v: 1; type: "ctx.update"; payload: Partial<CtxSnapshot> }
  | { v: 1; type: "auth.token"; payload: { token: string; expiresAt?: number } }
  | { v: 1; type: "theme"; payload: ThemeTokens }
  | { v: 1; type: "resize"; payload: { w: number; h: number } };

// app → host -----------------------------------------------------------------
export type AppToHost =
  | { v: 1; type: "ready" }
  | { v: 1; type: "state.push"; payload: { routeId: string; params: Record<string, string> } }
  | { v: 1; type: "action.navigate"; payload: { dashboardId?: string; stateId: string; params: Record<string, string> } }
  | { v: 1; type: "action.openEntity"; payload: { entityType: string; entityId: string } }
  | { v: 1; type: "action.custom"; payload: { name: string; args: unknown } }
  | { v: 1; type: "height"; payload: { px: number } }
  | { v: 1; type: "auth.refreshNeeded" };

const HOST_TYPES = new Set(["ctx.init", "ctx.update", "auth.token", "theme", "resize"]);

function isEnvelope(msg: unknown): msg is { v: unknown; type: unknown } {
  return !!msg && typeof msg === "object" && "v" in (msg as object) && "type" in (msg as object);
}

export function isHostToApp(msg: unknown): msg is HostToApp {
  if (!isEnvelope(msg)) return false;
  return msg.v === 1 && typeof msg.type === "string" && HOST_TYPES.has(msg.type as string);
}

export const EMPTY_CTX: CtxSnapshot = {
  entity: null,
  aliasId: null,
  stateParams: {},
  timeWindow: null,
  settings: {},
  userInfo: null,
  locale: null,
};
